import type { RouteDef } from '@/app/routes'
import type { WrittenPage } from '../sections'

/**
 * The "How Grocery Retail Leaders Are Preparing the Shopping Season" e-book's
 * own page.
 *
 * Built to the design of the digitopia.co page it replaces
 * (`/impact-labs/ebooks/ai-grocery-shopping/`), sent as four screenshots on
 * 2026-08-28 with *"referans alarak ilgili sayfayı tastamam şekilde, aynısını,
 * tıpkısını tasarla"*. Band for band that page's: the two-column hero with the
 * supermarket aisle beside it, the grey "Why Download?" card with the stacked
 * copies and two paragraphs, the "What You'll Learn" card with the single
 * mockup and four ticks, the centred "Highlights" card, and the centred closing
 * ask. The copy is that page's copy, verbatim.
 *
 * Sixth e-book page, and the twin of `ebook-ai-in-fashion-retail.ts` — the two
 * shopping-season books share a design, a photographer's brief and three of
 * their four departures from the reference:
 *
 *  * **The eyebrow is the site's own**, over the copy column, rather than
 *    digitopia.co's small left-pinned label. Every other hero here is that
 *    shape.
 *  * **The download is a direct link to the PDF, not a gated form.** The
 *    reference puts a nine-field HubSpot form between the reader and the file;
 *    no form on this site submits anywhere yet, so reproducing it would mean
 *    the page's only call to action silently drops every lead. The `check`
 *    below says so, as the sibling pages' do.
 *  * **A testimonial closes the page**, which the reference does not have — it
 *    is what the sibling e-book pages do, asked for again on 2026-08-28
 *    (*"en aşağıda önceden konuştuğumuz gibi testimonial template ini
 *    kullanalım"*). The card is A101's: of the twenty in
 *    `content/testimonials.ts` it is the only **grocery retailer**, and Orhan
 *    Dağlıoğlugil is talking about customer experience, operational improvement
 *    and retail-sector dynamics — this book's own subject. Both the logo and
 *    his portrait are already in the repository from the Hall of Fame, so the
 *    request's *"daha uygun daha kalitelisi varsa onu tercih et"* is answered by
 *    what is already here rather than by attaching a new face. His words are the
 *    Hall of Fame's; that file is where a change to them belongs.
 *  * **The Highlights card ends on its button.** The reference prints
 *    "Download to get the full playbook and benchmark your strategy." between
 *    its four bullets and the button, and a `cta` card renders prose *above*
 *    its points, not below — so the line would have to move in front of the
 *    list it follows. The closing band under the card already makes the same
 *    ask in more words, so it is dropped rather than reordered.
 *
 * **The slug is confirmed, and it is not the one the catalogue derived.**
 * `content/ebooks.ts` guessed the whole title, kebab-cased; the screenshots came
 * from `digitopia.co/impact-labs/ebooks/ai-grocery-shopping/`, so the real
 * address is the short one. The catalogue entry was corrected to match, which
 * also closed that e-book's slug `check` — the fifth time the title rule has
 * been caught out, after Sustainability Innovation, Measurement, AI in Fashion
 * Retail and the Leader's Guide to Generative AI.
 */
export const route: RouteDef = {
  id: 'ebookAiGroceryShopping',
  path: 'impact-labs/ebooks/ai-grocery-shopping',
  priority: 0.7,
}

/**
 * The file itself, in the `website-documents` bucket — a local constant rather
 * than a key in `content/pages/links.ts`, which is only for CTAs shared between
 * pages. The object key is `content/ebooks.ts`' own, with its one space
 * percent-encoded.
 */
export const PDF
  = 'https://njdxtkezxgkrmwwhzcne.supabase.co/storage/v1/object/public/website-documents/ebook/'
    + '13db563d-e647-4379-b1b8-2e81c1ea7010-Lead%20the%20Shopping%20Season%20with%20AI-Powered%20Strategies.pdf'

export const ebookAiGroceryShopping: WrittenPage = {
  navLabel: 'AI Grocery Shopping',
  title: 'Lead the Shopping Season with AI-Powered Strategies',
  description:
    'Walmart, Kroger, Target and Amazon Fresh are already setting the pace. See how grocery retail leaders turn digital investment into measurable performance. Download the e-book from Digitopia Impact Labs.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'How Retail Leaders Are Preparing the Season',
      headline: 'Lead the Shopping Season with AI-Powered Strategies',
      body: [
        'Walmart, Kroger, Target, and Amazon Fresh are already setting the pace.',
        'Discover how grocery retail leaders are turning digital investments into measurable '
          + 'performance. See what they’re doing differently and how you can catch up.',
      ],
      ctas: [{ label: 'Download', href: PDF }],
      photo: {
        src: '/photo/ai-grocery-shopping.webp',
        alt:
          'A red shopping basket holding a pineapple, bananas and a bottle of juice, '
          + 'photographed low down a supermarket aisle with the shelves blurred behind it.',
      },
      // The photograph is square and the site's hero box is 3:2, which would
      // cut the top off the basket.
      photoFrame: 'square',
      /*
        Clause 1 — THE DOWNLOAD IS NOT GATED — resolved 2026-08-30. It said no
        form on this site submits anywhere and the Send button is disabled on
        purpose. Both stopped being true today: the form posts to
        `website-form-lead`, the submission opens a task on its page's own
        counterpart, and every Download here carries `data-gated-download`, so
        the reader is asked for an email and the PDF is sent to it. No HubSpot
        form id is needed for any of it. What is left is clause 2.
      */
      check:
        'THE ARTWORK IS 768–900px WIDE. Those are the largest copies of the '
        + 'aisle photograph and the two cover mockups anybody has sent; every other '
        + 'hero on the site is 2560px, so this one is painted at roughly its own '
        + 'width on a laptop with nothing left over for a retina screen. If bigger '
        + 'exports exist wherever the covers were made:\n'
        + '     node apps/website/scripts/attach.mjs <file> photo-src/ai-grocery-shopping.webp\n'
        + '     pnpm --filter @digitopia/website images\n'
        + '   and nothing in this file changes.',
    },
    {
      kind: 'cta',
      tone: 'card',
      // Mockup left, offer right — the reference's order, and it alternates with
      // the card under it.
      flip: true,
      heading: 'Why Download?',
      body: [
        'The shopping season can make or break grocery retailers. But promotions and shelf '
          + 'space alone no longer decide the winners.',
        'Success now depends on AI-driven execution, digital readiness, and frontline '
          + 'enablement. Leaders are already operationalising what others are still testing. '
          + 'This is the new era of AI grocery shopping, where strategy becomes execution.',
      ],
      ctas: [{ label: 'Download the Ebook', href: PDF }],
      photo: {
        src: '/photo/ai-grocery-shopping-ebook-covers.webp',
        alt:
          'Three copies of the grocery retail e-book, one behind the other, on a white '
          + 'background.',
      },
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'What You’ll Learn',
      sub: 'Inside this short analysis, you’ll discover:',
      points: [
        'Why execution has overtaken experimentation, with AI pilots now live at scale',
        'How frontline teams are being digitally empowered to act in real time',
        'Why inventory, promotions, and loyalty strategies must work together',
        'How leaders are turning seasonal spikes into measurable performance',
      ],
      ctas: [{ label: 'Download the Ebook', href: PDF }],
      photo: {
        src: '/photo/ai-grocery-shopping-ebook-cover.webp',
        alt:
          'The grocery retail e-book standing upright, its cover on a black and pink '
          + 'background.',
      },
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'Highlights',
      sub: 'In the report, you’ll see how:',
      points: [
        'Walmart is transforming stores into fulfilment hubs',
        'Kroger is using AI and cloud insights to sharpen operations',
        'Target is rolling out generative AI to its store teams',
        'Amazon Fresh is redefining loyalty with Prime-linked data',
      ],
      ctas: [{ label: 'Download the Ebook', href: PDF }],
    },
    {
      kind: 'prose',
      align: 'center',
      heading: 'Benchmark Your Strategy with AI Grocery Shopping Leaders',
      sub:
        'Learn how leading grocery retailers are preparing for the shopping season and '
        + 'benchmark your approach.',
      cta: { label: 'Download the Ebook', href: PDF },
    },
    {
      // The reference carries this line under its form, and it is the reason the
      // four brand names can be on the page at all. It stays.
      kind: 'prose',
      align: 'center',
      paragraphs: [
        'This report is for informational and benchmarking purposes only. Digitopia is not '
        + 'affiliated with or endorsed by the brands mentioned.',
      ],
    },
    {
      kind: 'quotes',
      variant: 'fame',
      heading: 'What our customers say.',
      items: [
        {
          // The Hall of Fame's A101 card, word for word —
          // `content/testimonials.ts` is where these words are edited, and the
          // Turkish page carries its own saying of them.
          quote:
            'We have been busy with digital transformation at A101 for the last 2 years '
            + 'focusing on the different dimensions of customer experience, operational '
            + 'improvements, innovation, and digital culture. Digitalisation is happening in '
            + 'all aspects of daily life. We wish to adapt to that and build a roadmap in '
            + 'consideration of country dynamics, retail sector dynamics and our corporate '
            + 'culture.',
          source: 'Orhan Dağlıoğlugil, CTO',
          company: 'A101',
          logo: { name: 'A101', src: '/logo/a101card.png' },
          portrait: { src: '/photo/portrait-orhan-daglioglugil.png', alt: 'Orhan Dağlıoğlugil' },
        },
      ],
    },
  ],
}
